import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import { Main } from "../main/Main";
import { Navigation } from "../navigation/Navigation";
import { LeftDrawer } from "./LeftDrawer";
import { Footer } from "../footer/Footer";
import { Dialog } from "components/common";
import { IdleWarningDialog } from "components/common/session/IdleWarningDialog";

import { promptedRoute, SlideRoute } from "route/SlideRoute";

import { useRootDispatch, useRootSelector } from "store/hooks";
import { clearSlide } from "store/common/common-slice";

import classes from "./Cockpit.module.scss";
import { useSearchParams, useLocation } from "react-router-dom";
import { clearSlideoutNotifications } from "store/notification/notification-slice";
import { isQuickPayRoute } from "utility";
import { SESSION_IDLE_TIMEOUT_MS } from "constants/session";

interface CockpitProps {
  onAuthenticatedIdleLogout: () => void;
  onQuickPayIdleLogout: () => void;
}

export const Cockpit = ({
  onAuthenticatedIdleLogout,
  onQuickPayIdleLogout,
}: CockpitProps) => {
  const { isSlideOpen, isNavOpen, pageDetail } = useRootSelector(
    (state) => state.common,
  );
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [searchParams] = useSearchParams();
  const [showDialog, setShowDialog] = useState(false);
  const { t } = useTranslation(["app"]);
  const refDrawer = useRef<any>();
  const dispatch = useRootDispatch();
  const location = useLocation();

  useEffect(() => {
    const successParam = searchParams.get("success");
    const codeParam = searchParams.get("code");

    if (successParam === "true" && codeParam === "success") {
      loginWithRedirect();
    }
  }, [searchParams, loginWithRedirect]);

  useEffect(() => {
    if (refDrawer.current) refDrawer.current.open = isSlideOpen;
  }, [isSlideOpen]);

  const isOnQuickPayRoute = isQuickPayRoute(location.pathname);

  const closeHandler = useCallback(
    (e: Event) => {
      if (refDrawer.current === e.target) {
        const res = promptedRoute.filter((single) => {
          return pageDetail?.id === single;
        });

        if (res.length > 0) {
          e.stopImmediatePropagation();
          e.stopPropagation();
          setShowDialog(true);
        }
      }
    },
    [pageDetail],
  );

  useEffect(() => {
    const drawer = refDrawer.current;
    drawer.removeEventListener("click", closeHandler, { capture: true });
    drawer.addEventListener("click", closeHandler, { capture: true });
    return () => {
      drawer.removeEventListener("click", closeHandler, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeHandler]);

  useEffect(() => {
    const drawer = refDrawer.current;
    drawer.addEventListener("MDCDrawer:closed", () => {
      dispatch(clearSlide());
      dispatch(clearSlideoutNotifications());
    });
    return () => {
      drawer.removeEventListener("MDCDrawer:closed", () => {
        dispatch(clearSlide());
        dispatch(clearSlideoutNotifications());
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dialogAction = () => {
    setShowDialog(false);
    dispatch(clearSlide());
  };

  const handleIdleLogout = isOnQuickPayRoute
    ? onQuickPayIdleLogout
    : onAuthenticatedIdleLogout;

  return (
    <>
      {(isAuthenticated || isOnQuickPayRoute) && (
        <IdleWarningDialog
          enabled
          onLogout={handleIdleLogout}
          timeout={SESSION_IDLE_TIMEOUT_MS}
        />
      )}
      {isAuthenticated && !isOnQuickPayRoute ? <LeftDrawer /> : null}
      <ft-drawer
        ref={refDrawer}
        class={[classes.Drawer, isNavOpen ? classes.NavOpen : ""].join(" ")}
        type="modal"
      >
        <SlideRoute />
        <div className={classes.AppContent} slot="appContent">
          <Navigation />
          <Suspense fallback={<ft-screen-loader></ft-screen-loader>}>
            <Main />
          </Suspense>
          <Footer />
        </div>
      </ft-drawer>
      {showDialog ? (
        <Dialog
          icon="warning_amber"
          primaryActionLabel={t("app:close_label")}
          primaryActionHandler={dialogAction}
          dialogClosed={() => setShowDialog(false)}
          type="warning"
          closeIcon
        >
          {t("app:reload_site")}
        </Dialog>
      ) : null}
    </>
  );
};
